import { useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { StatusDot } from '@/design-system/StatusDot';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SampleAdherence } from '@/versions/v1/schema/unit-operation';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { SAMPLE_ADHERENCE_CHIP } from '@/versions/v1/shared/StateChip';
import { MEETING_SHIFT_HANDOVER_TAB } from '@/versions/v1/modules/meeting/MeetingBoard.transform';
import { GmMiniRow, LandingStripTabs } from './GmLeadBand';
import { GmCard, statePillClass } from './GmTabs';
import {
  opsAbnormal,
  opsActionsCard,
  opsCarriedCard,
  opsChecks,
  opsComplianceBand,
  opsEquipmentRows,
  opsQueue,
  opsRoundsGrid,
  opsSampleGrid,
  opsSampleLegend,
  opsShiftClock,
  opsStatusBand,
  opsStripCells,
  opsSubtitle,
  type OpsAbnormal,
  type OpsCaseRow,
  type OpsGrid,
  type OpsQueueItem,
  type OpsTabId,
} from './OpsSupervisorLanding.transform';

type OpenCase = (caseId: string) => void;

// ---- Primitives on the comp's recipe ------------------------------------------------

/** Soft status pill on the state tokens: the GM landing's `statePillClass`
 *  (comp `.pill`). An in-band `ok` reads as the quiet neutral pill. */
function pillClass(tone: VizTone | 'neutral'): string {
  return statePillClass(tone === 'bad' || tone === 'warn' || tone === 'info' ? tone : 'neutral');
}

const TONE_TEXT: Record<'ok' | 'warn' | 'bad' | 'info', string> = {
  ok: 'text-[var(--state-online)]',
  warn: 'text-[var(--state-warning)]',
  bad: 'text-[var(--state-blocked)]',
  info: 'text-[var(--state-info)]',
};

/** Outlined severity pill for a case (comp `.pill.p-out`). */
function OutlinePill({ label, tone }: { label: string; tone: VizTone }) {
  const text = tone === 'ok' || tone === 'warn' || tone === 'bad' || tone === 'info' ? TONE_TEXT[tone] : 'text-ink-secondary';
  return (
    <span className={cn('inline-flex items-center whitespace-nowrap rounded-full border border-current px-1.5 text-[10px] font-semibold uppercase leading-[15px] tracking-[0.05em]', text)}>
      {label}
    </span>
  );
}

/**
 * The seven SampleAdherence chips (comp `.sa`, B's chip rules): colour is
 * abnormal, so REG is a quiet tick with no fill; LIMS?, LATE, MISS, DUE and
 * UPCOMING keep their chips; not scheduled is a dash. Labels are the shared
 * `SAMPLE_ADHERENCE_CHIP` set (adjudication 8).
 */
function SampleChip({ state, compact = false }: { state: SampleAdherence; compact?: boolean }) {
  const label = SAMPLE_ADHERENCE_CHIP[state].label;
  if (state === 'N/A') return <span className="text-xs text-muted" aria-label="Not scheduled">—</span>;
  const base = cn(
    'inline-flex items-center justify-center whitespace-nowrap rounded-full px-[7px] py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]',
    !compact && 'min-w-[52px]',
  );
  switch (state) {
    case 'REG':
      return <span className={cn(base, 'text-[var(--state-online)]')} data-ops-chip="REG">✓ {label}</span>;
    case 'PENDING':
    case 'LATE':
      return <span className={cn(base, 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]')} data-ops-chip={state}>{label}</span>;
    case 'MISS':
      return (
        <span
          className={cn(base, 'border border-[color-mix(in_srgb,var(--state-blocked)_40%,transparent)] bg-[color-mix(in_srgb,var(--state-blocked)_10%,transparent)] text-[var(--state-blocked)]')}
          data-ops-chip="MISS"
        >
          {label}
        </span>
      );
    case 'DUE':
      return <span className={cn(base, 'bg-[color-mix(in_srgb,var(--state-info)_11%,transparent)] text-[var(--state-info)]')} data-ops-chip="DUE">{label}</span>;
    default:
      return <span className={cn(base, 'border border-dashed border-[var(--hairline-strong)] text-muted')} data-ops-chip="UPCOMING">{label}</span>;
  }
}

/** A unit's status (comp `.st`): RUN is a quiet dot, WARN is the only unit colour. */
function StatusMark({ status }: { status: 'RUN' | 'WARN' }) {
  const warn = status === 'WARN';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[5px] whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.05em]',
        warn ? 'text-[var(--state-warning)]' : 'text-muted',
      )}
    >
      <span aria-hidden="true" className="inline-flex">
        <StatusDot state={warn ? 'warning' : 'online'} />
      </span>
      {warn ? 'Warn' : 'Run'}
    </span>
  );
}

const LINK_BUTTON =
  'inline-flex items-center whitespace-nowrap rounded-lg border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2 py-px text-[11.5px] text-ink-secondary transition-colors hover:bg-[var(--surface-1)] hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

const GO_LINK =
  'whitespace-nowrap text-[11.5px] font-semibold text-ink-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

const CAPS = 'text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';

// ---- The lead band ----------------------------------------------------------------

function CarriedCard({ state, href }: { state: SessionState; href: string }) {
  const card = opsCarriedCard(state);
  return (
    <Link
      to={href}
      data-ops-carried
      aria-label={`3Ws carried in · ${card.open} open · Daily meeting`}
      className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.07em] text-muted">3Ws carried in</span>
        {/* Below 1400 px the card is too narrow for the label and the way on
            side by side; the arrow stays and the card's label names the meeting. */}
        <span className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-ink-primary">
          <span className="hidden min-[1400px]:inline">Daily meeting</span>
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </span>
      </span>
      <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
        <span data-ops-carried-figure>{card.open}</span>
        <span className="text-xs font-semibold tracking-normal text-muted">open</span>
      </span>
      <span className="text-xs leading-snug tabular-nums text-muted" data-ops-carried-comparison>
        from <span className="font-semibold text-ink-secondary">{card.fromCrew}</span>
        {card.owned ? ` · ${card.owned.count} owned by ${card.owned.owner}` : null}
      </span>
      <span className="mt-auto flex flex-wrap items-center gap-1 pt-1">
        <ActorChip actor="Human">{`${card.fromCrew} → ${card.toCrew}`}</ActorChip>
      </span>
    </Link>
  );
}

function OpsLeadBand({
  state,
  tab,
  onTabChange,
  meetingHref,
}: {
  state: SessionState;
  tab: OpsTabId;
  onTabChange: (tab: OpsTabId) => void;
  meetingHref: string;
}) {
  // The anchor sits on the band's own element: a Commentable around the
  // component would stamp a prop the component never renders.
  return (
    <Commentable name="Kpis">
    <div className="grid grid-cols-12 gap-3" data-ops-lead-band>
      <GlassCard
        as="nav"
        tone="matte"
        aria-label="Landing views"
        className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
      >
        <LandingStripTabs
          cells={opsStripCells(state)}
          selected={tab}
          onSelect={onTabChange}
          dataPrefix="ops"
          panelId="ops-landing-panel"
          columnsClassName="sm:grid-cols-3"
        />
      </GlassCard>
      <CarriedCard state={state} href={meetingHref} />
    </div>
    </Commentable>
  );
}

// ---- Before handover --------------------------------------------------------------

function QueueTag({ item }: { item: OpsQueueItem }) {
  const tag = item.tag;
  if (tag.kind === 'priority') return <span className={pillClass(tag.tone)}>{tag.label}</span>;
  if (tag.kind === 'chip') return <SampleChip state={tag.state} compact />;
  return tag.status === 'in-progress' ? (
    <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--state-info)]">In progress</span>
  ) : (
    <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted">Open</span>
  );
}

function QueueRow({ item }: { item: OpsQueueItem }) {
  return (
    <div
      className="grid grid-cols-[44px_88px_minmax(0,1fr)_auto_auto] items-center gap-x-2.5 border-b border-[var(--color-divider)] px-4 py-[7px] text-[12.5px] last:border-b-0"
      data-ops-queue-item={item.id}
    >
      <span
        className={cn(
          'font-semibold tabular-nums',
          item.soon ? 'text-[var(--state-blocked)]' : 'text-ink-primary',
          item.due === null && 'text-[13px] font-normal',
        )}
      >
        {item.due ?? (
          <span
            aria-hidden="true"
            className={cn('inline-block w-3.5 text-center', item.tag.kind === 'task' && item.tag.status === 'in-progress' ? 'text-[var(--state-info)]' : 'text-muted')}
          >
            {item.tag.kind === 'task' && item.tag.status === 'in-progress' ? '⟳' : '○'}
          </span>
        )}
      </span>
      <span>
        <QueueTag item={item} />
      </span>
      <span className="min-w-0 leading-snug text-ink-primary" title={item.title}>
        {item.text}
      </span>
      <span>{item.owner ? <ActorChip actor="Human">{item.owner}</ActorChip> : null}</span>
      <span className="whitespace-nowrap text-[11px] text-muted">{item.source}</span>
    </div>
  );
}

function QueueCard({ state }: { state: SessionState }) {
  const queue = opsQueue(state);
  const clock = opsShiftClock(state);
  return (
    <GmCard flush title="Close before handover" meta={`from ${clock.now} · due order`} className="h-full" data-ops-queue>
      {queue.groups.map((group) => (
        <div key={group.id} data-ops-queue-group={group.id}>
          <div
            className={cn(
              'flex items-baseline gap-2 border-b border-[var(--color-divider)] bg-[var(--surface-1)] px-4 pb-[5px] pt-2',
              CAPS,
              'border-t border-t-[var(--hairline)] first:border-t-0',
            )}
          >
            {group.label}
            <span className="ml-auto text-[11px] font-medium normal-case tracking-[0.02em] tabular-nums">{group.items.length}</span>
          </div>
          {group.items.map((item) => (
            <QueueRow key={item.id} item={item} />
          ))}
        </div>
      ))}
      {queue.doneTasks > 0 ? (
        <p className="border-t border-[var(--hairline)] px-4 py-2 text-[11.5px] text-muted" data-ops-queue-done>
          {`${queue.doneTasks} ${queue.doneTasks === 1 ? 'task' : 'tasks'} done this shift`}
        </p>
      ) : null}
    </GmCard>
  );
}

function AckControl({ id, active, lead = '' }: { id: string; active: boolean; lead?: string }) {
  const { can } = useRole();
  const advisory = useAdvisory();
  if (active && can('alarm:ack')) {
    return (
      <button type="button" className={LINK_BUTTON} onClick={() => advisory.ackAlarm(id)}>
        Ack
      </button>
    );
  }
  return <span className="text-[11px] text-muted">{`${lead}${active ? 'active' : 'acked'}`}</span>;
}

function SampleRequestLine({ row, stacked = false }: { row: OpsAbnormal['sampleRequests'][number]; stacked?: boolean }) {
  const advisory = useAdvisory();
  const text = `${row.readingLabel} — last ${row.pointCount} assays walking away from ${row.basisLabel} · still in band`;
  return (
    <div className={cn(stacked ? 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1' : 'flex items-center gap-1.5')} data-ops-sample-request={row.id}>
      <span className={cn(stacked && 'col-span-2')}>
        <span className={pillClass('warn')}>Sample</span>
      </span>
      <span className={cn('min-w-0 text-[12px] leading-snug text-ink-primary', stacked ? 'col-span-2' : 'flex-1')}>{text}</span>
      {stacked ? <span className="text-[11px] tabular-nums text-muted">{row.detectedAt}</span> : null}
      {row.requested ? (
        <span className="whitespace-nowrap text-[11px] font-medium text-muted">
          {row.mode === 'campaign' && row.campaign
            ? `campaign ${row.requestedAt} · ${row.campaign.intervalMin} min × ${row.campaign.hours} h`
            : `requested ${row.requestedAt} · Met to chase`}
        </span>
      ) : (
        <button
          type="button"
          className={LINK_BUTTON}
          onClick={() => advisory.requestSamples(row.divergenceId, row.samplePointId)}
        >
          Get samples
        </button>
      )}
    </div>
  );
}

function CaseLine({ row, onOpenCase }: { row: OpsCaseRow; onOpenCase: OpenCase }) {
  return (
    <button
      type="button"
      onClick={() => onOpenCase(row.id)}
      className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-[7px] gap-y-px border-t border-[var(--color-divider)] py-[5px] text-left text-[12px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]"
      data-ops-case={row.code}
    >
      <OutlinePill label={row.chip.label} tone={row.chip.tone} />
      <span data-ops-code className="whitespace-nowrap font-mono text-[10.5px] tracking-[0.02em] text-ink-secondary">{row.code}</span>
      <span className="text-right text-[11.5px] font-semibold tabular-nums text-ink-primary">{row.confidence}</span>
      <span className="col-span-3 truncate leading-snug text-ink-primary">{row.title}</span>
      <span className="col-span-3 text-[10px] font-semibold uppercase tracking-[0.05em] text-muted">{row.state}</span>
    </button>
  );
}

const ABNORMAL_CASES_FIRST = 3;

function AbnormalCard({ state, onOpenCase }: { state: SessionState; onOpenCase: OpenCase }) {
  const advisory = useAdvisory();
  const model = useMemo(() => opsAbnormal(state, advisory), [state, advisory]);
  const [allCases, setAllCases] = useState(false);
  const cases = allCases ? model.cases : model.cases.slice(0, ABNORMAL_CASES_FIRST);
  const more = model.cases.length - ABNORMAL_CASES_FIRST;
  const EV = 'border-b border-[var(--color-divider)] px-4 py-2 text-[12px]';
  return (
    <Commentable name="AlarmPanel">
      <GmCard flush title="Abnormal now" meta={model.meta} className="h-full" data-ops-abnormal>
        {model.alarms.map((alarm) => (
          <div
            key={alarm.id}
            className={cn(EV, alarm.high && 'shadow-[inset_3px_0_0_var(--state-blocked)]')}
            data-ops-alarm={alarm.code}
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={pillClass(alarm.chip.tone)}>{alarm.chip.label}</span>
              <span data-ops-code className="whitespace-nowrap font-mono text-[11px] tracking-[0.01em] text-ink-secondary">{alarm.code}</span>
              <span className="flex-1" />
              <span className="text-[11px] tabular-nums text-muted">{alarm.raised}</span>
              <AckControl id={alarm.id} active={alarm.active} lead="· " />
            </div>
            <p className="mt-0.5 leading-snug text-ink-primary [text-wrap:pretty]">{alarm.message}</p>
          </div>
        ))}
        {model.zeroTouch.map((row) => (
          <div key={row.caseId} className={EV} data-ops-zero-touch={row.alarmCode}>
            <div className="flex flex-wrap items-center gap-1.5">
              <ActorChip actor="Agent" />
              <span className="font-semibold text-ink-primary">
                <span data-ops-code className="whitespace-nowrap">{row.alarmCode}</span>
                {` · ${row.action}`}
              </span>
              <span className="flex-1" />
              <button type="button" data-ops-code className={GO_LINK} onClick={() => onOpenCase(row.caseId)}>
                Open case
              </button>
            </div>
            <p className="mt-0.5 text-[11.5px] text-muted">
              {`to the ${row.routedTo} · ${row.branch}`}
              {row.controlMode ? (
                <>
                  {' · '}
                  <span data-ops-code className="whitespace-nowrap font-semibold text-ink-secondary">{row.controlMode}</span>
                </>
              ) : null}
            </p>
          </div>
        ))}
        {model.warnUnits.length > 0 ? (
          <div className={cn(EV, 'space-y-0.5')} data-ops-warn-units>
            {model.warnUnits.map((unit) => (
              <div key={unit.id} className="flex flex-wrap items-center gap-1.5" data-ops-warn-unit={unit.id}>
                <StatusMark status="WARN" />
                <span className="text-ink-primary">
                  {`${unit.name} · ${unit.param}`}
                  {unit.comparison ? <span className="text-muted">{` ${unit.comparison}`}</span> : null}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {model.misses.length > 0 || model.sampleRequests.length > 0 ? (
          <Commentable name="SampleRequests">
            <div className={cn(EV, 'space-y-1')} data-ops-samples>
              {model.misses.map((miss) => (
                <div key={`${miss.stream}-${miss.slot}`} className="flex flex-wrap items-center gap-1.5">
                  {miss.caseId ? (
                    <button type="button" className="inline-flex" onClick={() => onOpenCase(miss.caseId!)} aria-label={`${miss.stream} ${miss.slot} · missed`}>
                      <SampleChip state="MISS" compact />
                    </button>
                  ) : (
                    <SampleChip state="MISS" compact />
                  )}
                  <span className="text-ink-primary">{`${miss.stream} · ${miss.slot}`}</span>
                </div>
              ))}
              {model.sampleRequests.map((row) => (
                <SampleRequestLine key={row.id} row={row} />
              ))}
            </div>
          </Commentable>
        ) : null}
        {model.cases.length > 0 ? (
          <div className="px-4 pb-2" data-ops-cases>
            <div className={cn('flex items-center gap-1.5 pb-1 pt-2', CAPS)}>
              <ActorChip actor="Agent" />
            </div>
            {cases.map((row) => (
              <CaseLine key={row.id} row={row} onOpenCase={onOpenCase} />
            ))}
            {more > 0 ? (
              <button
                type="button"
                className="w-full border-t border-[var(--color-divider)] pt-1.5 text-left text-[11.5px] text-muted hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
                onClick={() => setAllCases((open) => !open)}
                aria-expanded={allCases}
                data-ops-cases-more
              >
                {allCases ? 'Fewer' : `${more} more`}
              </button>
            ) : null}
          </div>
        ) : null}
      </GmCard>
    </Commentable>
  );
}

function HandoverView({ state, onOpenCase }: { state: SessionState; onOpenCase: OpenCase }) {
  const advisory = useAdvisory();
  return (
    <div className="space-y-3">
      <div data-ops-band>
        <GmMiniRow items={opsStatusBand(state, advisory)} />
      </div>
      <div className="grid grid-cols-12 gap-3">
        <Commentable name="Primary">
          <div className="col-span-12 lg:col-span-7">
            <QueueCard state={state} />
          </div>
        </Commentable>
        <div className="col-span-12 lg:col-span-5">
          <AbnormalCard state={state} onOpenCase={onOpenCase} />
        </div>
      </div>
    </div>
  );
}

// ---- Equipment --------------------------------------------------------------------

const TH = 'whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-1.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';
const TD = 'border-b border-[var(--color-divider)] px-2 py-[5px] align-middle';

function EquipmentCard({ state }: { state: SessionState }) {
  const rows = opsEquipmentRows(state);
  const clock = opsShiftClock(state);
  return (
    <GmCard flush title="Equipment status board" meta={`DCS · ${clock.now}`} data-ops-equipment>
      <table className="w-full border-collapse text-[12.5px] tabular-nums">
        <thead>
          <tr>
            <th className={cn(TH, 'pl-4')}>Equipment</th>
            <th className={TH}>Status</th>
            <th className={TH}>Key param</th>
            <th className={cn(TH, 'text-right')}>Avail %</th>
            <th className={cn(TH, 'pr-4 text-right')}>Power</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const warn = row.status === 'WARN';
            return (
              <tr key={row.id} className="[&:last-child>td]:border-b-0" data-ops-equipment-row={row.id} data-ops-status={row.status}>
                <td className={cn(TD, 'pl-4 font-medium text-ink-primary', warn && 'shadow-[inset_3px_0_0_var(--state-warning)]')}>{row.name}</td>
                <td className={TD}>
                  <StatusMark status={row.status} />
                </td>
                <td className={cn(TD, warn ? 'text-[var(--state-warning)]' : 'text-ink-secondary')}>{row.param}</td>
                <td className={cn(TD, 'text-right text-ink-secondary')}>{row.avail}</td>
                <td className={cn(TD, 'pr-4 text-right text-ink-secondary')}>{row.power}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GmCard>
  );
}

function AlarmsCard({ state, onOpenCase }: { state: SessionState; onOpenCase: OpenCase }) {
  const advisory = useAdvisory();
  const model = useMemo(() => opsAbnormal(state, advisory), [state, advisory]);
  return (
    <Commentable name="AlarmPanel">
      <GmCard flush title="Alarms & advisories" meta={model.meta} data-ops-alarms>
        <table className="w-full border-collapse text-[12.5px] tabular-nums">
          <thead>
            <tr>
              <th className={cn(TH, 'pl-4')}>Sev</th>
              <th className={TH}>Code</th>
              <th className={TH}>Message</th>
              <th className={cn(TH, 'text-right')}>Raised</th>
              <th className={cn(TH, 'pr-4')} />
            </tr>
          </thead>
          <tbody>
            {model.alarms.map((alarm) => (
              <tr key={alarm.id} data-ops-alarm={alarm.code}>
                <td className={cn(TD, 'pl-4', alarm.high && 'shadow-[inset_3px_0_0_var(--state-blocked)]')}>
                  <span className={pillClass(alarm.chip.tone)}>{alarm.chip.label}</span>
                </td>
                <td data-ops-code className={cn(TD, 'whitespace-nowrap font-mono text-[11px] text-ink-secondary')}>{alarm.code}</td>
                <td className={cn(TD, 'text-ink-primary [text-wrap:pretty]')}>{alarm.message}</td>
                <td className={cn(TD, 'text-right text-ink-secondary')}>{alarm.raised}</td>
                <td className={cn(TD, 'pr-4 text-right')}>
                  <AckControl id={alarm.id} active={alarm.active} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {model.zeroTouch.map((row) => (
          <div key={row.caseId} className="space-y-1 border-t border-[var(--hairline)] px-4 py-2.5 text-[12px]" data-ops-zero-touch={row.alarmCode}>
            <div className="flex flex-wrap items-center gap-1.5">
              <span data-ops-code className="whitespace-nowrap font-mono text-[11px] text-ink-secondary">{row.alarmCode}</span>
              <span className="font-semibold text-ink-primary">{`Immediate response: ${row.action}`}</span>
              <span className="flex-1" />
              <button type="button" data-ops-code className={GO_LINK} onClick={() => onOpenCase(row.caseId)}>
                Open case
              </button>
            </div>
            <p className="text-[11.5px] text-muted">
              {`Routed to the ${row.routedTo} · ${row.branch}`}
              {row.controlMode ? (
                <>
                  {' · control mode '}
                  <span data-ops-code className="whitespace-nowrap font-semibold text-ink-secondary">{row.controlMode}</span>
                </>
              ) : null}
            </p>
            <div className="flex flex-wrap gap-1">
              <ActorChip actor="Agent" />
              <ActorChip actor="Human">{row.routedTo}</ActorChip>
            </div>
          </div>
        ))}
        {model.sampleRequests.length > 0 ? (
          <Commentable name="SampleRequests">
            <div className="border-t border-[var(--hairline)] px-4 py-2.5" data-ops-samples>
              {model.sampleRequests.map((row) => (
                <SampleRequestLine key={row.id} row={row} stacked />
              ))}
            </div>
          </Commentable>
        ) : null}
        {model.cases.length > 0 ? (
          <div className="border-t border-[var(--hairline)]" data-ops-cases>
            <p className={cn('px-4 pb-0.5 pt-2.5', CAPS)}>AI advisory cases</p>
            <table className="w-full border-collapse text-[12.5px] tabular-nums">
              <tbody>
                {model.cases.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer transition-colors hover:bg-[var(--surface-1)] [&:last-child>td]:border-b-0"
                    onClick={() => onOpenCase(row.id)}
                    data-ops-case={row.code}
                  >
                    <td className={cn(TD, 'w-[84px] pl-4')}>
                      <OutlinePill label={row.chip.label} tone={row.chip.tone} />
                      <div data-ops-code className="mt-0.5 whitespace-nowrap font-mono text-[10.5px] text-ink-secondary">{row.code}</div>
                    </td>
                    <td className={cn(TD, 'text-ink-primary')}>
                      <button
                        type="button"
                        className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenCase(row.id);
                        }}
                      >
                        {row.title} <span className="text-[11px] tabular-nums text-muted">{row.raised}</span>
                      </button>
                    </td>
                    <td className={cn(TD, 'text-[10px] font-semibold uppercase leading-snug tracking-[0.05em] text-muted min-[1400px]:whitespace-nowrap')}>{row.state}</td>
                    <td className={cn(TD, 'pr-4 text-right font-semibold text-ink-primary')}>{row.confidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </GmCard>
    </Commentable>
  );
}

function EquipmentView({ state, onOpenCase }: { state: SessionState; onOpenCase: OpenCase }) {
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <EquipmentCard state={state} />
        </div>
      </Commentable>
      <div className="col-span-12 lg:col-span-5">
        <AlarmsCard state={state} onOpenCase={onOpenCase} />
      </div>
    </div>
  );
}

// ---- Shift compliance --------------------------------------------------------------

function GridTable({ grid, onOpenCase, dataKey }: { grid: OpsGrid; onOpenCase: OpenCase; dataKey: string }) {
  const firstHead = grid.caption.startsWith('Sample') ? 'Stream' : 'Area';
  return (
    <table className="w-full border-collapse text-[12.5px] tabular-nums" {...{ [`data-ops-${dataKey}`]: true }}>
      <thead>
        <tr>
          <th className={cn(TH, 'pl-4')}>{firstHead}</th>
          {grid.columns.slice(1).map((slot) => (
            <th key={slot} className={cn(TH, 'text-center last:pr-4', slot === grid.dueSlot && 'text-[var(--state-info)]')}>
              {slot}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {grid.rows.map((row) => (
          <tr key={row.id} className={cn(!grid.footer && '[&:last-child>td]:border-b-0')}>
            <td className={cn(TD, 'pl-4 font-medium text-ink-primary')}>{row.label}</td>
            {row.cells.map((cell, index) => (
              <td key={`${row.id}-${index}`} className={cn(TD, 'text-center last:pr-4')}>
                {cell.state ? (
                  cell.caseId ? (
                    <button
                      type="button"
                      className="inline-flex"
                      onClick={() => onOpenCase(cell.caseId!)}
                      aria-label={`${row.label} ${grid.columns[index + 1]} · missed`}
                    >
                      <SampleChip state={cell.state} />
                    </button>
                  ) : (
                    <SampleChip state={cell.state} />
                  )
                ) : (
                  cell.text
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {grid.footer ? (
        <tfoot>
          <tr className="bg-[var(--surface-1)]">
            <td className="border-t border-[var(--hairline-strong)] px-2 py-[5px] pl-4 font-semibold text-ink-primary">Compliance</td>
            {grid.footer.map((cell, index) => (
              <td
                key={index}
                className={cn(
                  'whitespace-nowrap border-t border-[var(--hairline-strong)] px-2 py-[5px] text-center font-semibold last:pr-4',
                  cell.state || cell.text !== '—' ? 'text-ink-primary' : 'font-medium text-muted',
                )}
              >
                {cell.state ? <SampleChip state={cell.state} /> : cell.text}
              </td>
            ))}
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
}

function SampleLegend() {
  return (
    <div aria-label="Sample state legend" className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pb-2 pt-2 text-[11px] text-muted">
      {opsSampleLegend().map((entry) => (
        <span key={entry.state} className="inline-flex items-center gap-[5px]">
          <SampleChip state={entry.state} compact />
          {entry.meaning}
        </span>
      ))}
    </div>
  );
}

function ActionsCard() {
  const card = opsActionsCard();
  return (
    <GmCard flush title="Outstanding actions" meta={card.meta} data-ops-actions>
      <table className="w-full border-collapse text-[12.5px] tabular-nums">
        <thead>
          <tr>
            <th className={cn(TH, 'pl-4')}>Priority</th>
            <th className={TH}>Action · who</th>
            <th className={cn(TH, 'pr-4 text-right')}>Due</th>
          </tr>
        </thead>
        <tbody>
          {card.rows.map((row) => (
            <tr key={row.id} className="[&:last-child>td]:border-b-0" data-ops-action={row.id}>
              <td className={cn(TD, 'pl-4 py-[7px]')}>
                <span className={pillClass(row.tone)}>{row.priority}</span>
              </td>
              <td className={cn(TD, 'py-[7px] font-medium text-ink-primary')}>
                {row.text}
                <div className="mt-[3px]">
                  <ActorChip actor="Human">{row.who}</ActorChip>
                </div>
              </td>
              <td className={cn(TD, 'whitespace-nowrap pr-4 py-[7px] text-right font-semibold text-ink-primary')}>{row.due}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GmCard>
  );
}

function ChecksCard() {
  const checks = opsChecks();
  return (
    <GmCard flush title="Shift checks" data-ops-checks>
      <table className="w-full border-collapse text-[12.5px]">
        <tbody>
          {checks.map((check) => (
            <tr key={check.id} className="[&:last-child>td]:border-b-0" data-ops-check={check.id}>
              <td className={cn(TD, 'w-[84px] pl-4')}>
                <SampleChip state={check.state} />
              </td>
              <td className={cn(TD, 'pr-4 text-ink-primary')}>{check.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GmCard>
  );
}

/** The Shift compliance view: its band, the sample and rounds grids, the
 *  outstanding actions and the shift checks — the operating-discipline record
 *  formerly on `/compliance` (A7). */
function ShiftComplianceView({ state, onOpenCase }: { state: SessionState; onOpenCase: OpenCase }) {
  const sample = opsSampleGrid(state);
  const rounds = opsRoundsGrid();
  return (
    <div className="space-y-3">
      <div data-ops-band>
        <GmMiniRow items={opsComplianceBand(state)} />
      </div>
      <div className="grid grid-cols-12 gap-3">
        <Commentable name="Primary">
        <div className="col-span-12 space-y-3 lg:col-span-8">
          <GmCard flush title={sample.caption} meta={sample.meta}>
            <SampleLegend />
            <div className="overflow-x-auto">
              <GridTable grid={sample} onOpenCase={onOpenCase} dataKey="sample-grid" />
            </div>
          </GmCard>
          <GmCard flush title={rounds.caption} meta={rounds.meta}>
            <div className="overflow-x-auto">
              <GridTable grid={rounds} onOpenCase={onOpenCase} dataKey="rounds-grid" />
            </div>
          </GmCard>
        </div>
        </Commentable>
        <Commentable name="Secondary">
          <div className="col-span-12 space-y-3 lg:col-span-4">
            <ActionsCard />
            <ChecksCard />
          </div>
        </Commentable>
      </div>
    </div>
  );
}

// ---- Composition ------------------------------------------------------------------

/**
 * The Ops supervisor's landing (polish.P13 #700, locked comp
 * `landing-ops-supervisor.html` C with A's shift rule and B's chip rules): the
 * GM landing's anatomy at shift scope. Three strip cells are the tab bar,
 * Before handover is the default view, and the carried-in 3Ws step onward to
 * the Daily meeting.
 */
export function OpsSupervisorLanding({
  state,
  projectSlug,
  onOpenCase,
}: {
  state: SessionState;
  projectSlug: string;
  onOpenCase: OpenCase;
}) {
  const [tab, setTab] = useState<OpsTabId>('handover');
  return (
    <div className="space-y-3" data-ops-landing>
      <p className="-mt-3 text-sm text-muted">{opsSubtitle(state)}</p>
      <OpsLeadBand
        state={state}
        tab={tab}
        onTabChange={setTab}
        meetingHref={`${projectSidebarHref(projectSlug, 'meeting')}/${MEETING_SHIFT_HANDOVER_TAB}`}
      />
      {/* Each view anchors its own regions at the Body level (Primary,
          Secondary, AlarmPanel), the paths `anchors.ts` registers. */}
      <div id="ops-landing-panel" role="tabpanel" aria-labelledby={`ops-tab-${tab}`}>
        {tab === 'handover' ? <HandoverView state={state} onOpenCase={onOpenCase} /> : null}
        {tab === 'equipment' ? <EquipmentView state={state} onOpenCase={onOpenCase} /> : null}
        {tab === 'compliance' ? <ShiftComplianceView state={state} onOpenCase={onOpenCase} /> : null}
      </div>
    </div>
  );
}
